/**
 * The status mark — one component for "where is this object?" across every
 * Body section (Body-tab XD note 2026-09-27 §3.1; Allen ruled Q1 the same day:
 * standardise the MARKS, not the colours).
 *
 * TWO CHANNELS, carried forward from finger pinning (hand-markers.ts):
 *  - HUE = WHICH OBJECT. Passed only when the object has a twin marker on the
 *    model (a digit, the wrist, the elbow); otherwise the mark is neutral.
 *  - FORM = WHO SAID IT, AND HOW SURE:
 *      needs     ◌ dashed hollow ring — the open question (the purple line names the step)
 *      suggested ○ hollow ring        — the engine's advice; never blocks
 *      proposed  • dot, no ring, 65 % — the pre-fill's answer, not yours yet
 *      yours     ◉ dot + ring         — a person decided (the lock)
 *      fine      · small grey dot     — nothing to do (in the file, the baseline)
 *      check     ◉ + halo             — your answer stands; the evidence disagrees
 * No green "done", no amber "warn": a ring already says a person decided, in any hue.
 *
 * The PILL is the small word beside it; it states provenance (who decided), never urgency.
 *
 * Depends on: nothing. Depended on by: the Body panels (header marks, card heads).
 */

export const STATUS_STATES = ['needs', 'suggested', 'proposed', 'yours', 'fine', 'check'] as const;
export type StatusState = (typeof STATUS_STATES)[number];

/** The words a screen reader hears, and the default pill per state (null = no pill). */
const WORDS: Readonly<Record<StatusState, { label: string; pill: string | null }>> = {
  needs: { label: 'needs you', pill: null },
  suggested: { label: 'suggested', pill: 'suggested' },
  proposed: { label: 'proposed', pill: 'proposed' },
  yours: { label: 'set by you', pill: null },
  fine: { label: 'fine as is', pill: null },
  check: { label: 'check', pill: 'check' },
};

export interface StatusMarkOptions {
  /** The object's identity hue as a CSS colour or token (`var(--hue-thumb)`); omit for a neutral mark. */
  readonly hue?: string;
  /** The pill's word; `undefined` = the state's default, `null` = no pill (`set`, `in file`, `baseline`…). */
  readonly pill?: string | null;
  /** What the mark is about, for the accessible name (`Left hand`). */
  readonly subject?: string;
}

/** The pure part: what the mark IS (testable without a DOM). */
export interface StatusMarkSpec {
  readonly state: StatusState;
  readonly hue: string | null;
  readonly pill: string | null;
  readonly label: string;
}

export function statusMarkSpec(state: StatusState, options: StatusMarkOptions = {}): StatusMarkSpec {
  const words = WORDS[state];
  const pill = options.pill === undefined ? words.pill : options.pill;
  return {
    state,
    hue: options.hue ?? null,
    pill,
    label: options.subject === undefined ? words.label : `${options.subject}: ${words.label}`,
  };
}

/** The mark (and its pill, when it has one) as one inline element. */
export function statusMark(state: StatusState, options: StatusMarkOptions = {}): HTMLElement {
  const spec = statusMarkSpec(state, options);
  const wrap = document.createElement('span');
  wrap.className = 'status';
  wrap.dataset['state'] = spec.state;
  const mark = document.createElement('span');
  mark.className = 'status-mark';
  mark.dataset['state'] = spec.state;
  mark.setAttribute('role', 'img');
  mark.setAttribute('aria-label', spec.label);
  if (spec.hue !== null) mark.style.setProperty('--mark-hue', spec.hue);
  wrap.append(mark);
  if (spec.pill !== null) {
    const pill = document.createElement('span');
    pill.className = 'status-pill';
    pill.dataset['state'] = spec.state;
    pill.textContent = spec.pill;
    wrap.append(pill);
  }
  return wrap;
}
